import SEO from '../components/SEO';
import React from 'react';
import Navigation from '../components/Navigation';
import NewsletterSignup from '../components/NewsletterSignup';
import SiteFooter from '../components/SiteFooter';
import BenefitsCalculator from '../components/BenefitsCalculator';
import NewsHighlights from '../components/NewsHighlights';

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO title="Maximize Your Credit Card Benefits & Employee Perks" description="Expert guidance to unlock thousands in credit card benefits and employee perks. Compare job offers, understand 401k matching, HSA plans, and total compensation." path="/" keywords="employee benefits comparison, job offer benefits comparison, total compensation calculator, employee benefits package, credit card benefits, employee perks, benefits optimization" />
      
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-hero py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            Maximize Your Benefits.<br/>Minimize Your Costs.
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90 max-w-2xl mx-auto">
            Smart benefits management for modern employees
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/#calculator" className="inline-block bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-xl font-bold text-lg shadow-xl transition-all">
              Calculate My Savings
            </a>
            <a href="/articles" className="inline-block bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-xl font-bold text-lg transition-all">
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-blue-600 font-mono mb-2">$12K+</div>
              <div className="text-sm text-gray-600 uppercase tracking-wide">Avg. Annual Savings</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-blue-600 font-mono mb-2">50K+</div>
              <div className="text-sm text-gray-600 uppercase tracking-wide">Happy Employees</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-blue-600 font-mono mb-2">500+</div>
              <div className="text-sm text-gray-600 uppercase tracking-wide">Companies</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-blue-600 font-mono mb-2">99%</div>
              <div className="text-sm text-gray-600 uppercase tracking-wide">Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      <NewsHighlights />

      {/* Benefits Section - keeping same content */}
      <section id="benefits" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-gray-900">
            Your Complete Benefits Package
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-2xl mx-auto">
            Optimize every aspect of your employee benefits
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Healthcare */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:border-blue-600 hover:shadow-xl transition-all hover-lift">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-400 rounded-2xl flex items-center justify-center text-white mb-6">
                <svg className="h-10 w-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Healthcare</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Medical, dental, and vision coverage options with HSA and FSA accounts.
              </p>
              <div className="text-green-600 font-mono font-bold text-lg">
                Save up to $4,500/year
              </div>
            </div>

            {/* Retirement */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:border-blue-600 hover:shadow-xl transition-all hover-lift">
              <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-green-400 rounded-2xl flex items-center justify-center text-white mb-6">
                <svg className="h-10 w-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Retirement</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                401(k), Roth IRA, and pension plans with employer matching.
              </p>
              <div className="text-green-600 font-mono font-bold text-lg">
                Save up to $6,000/year
              </div>
            </div>

            {/* Education */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:border-blue-600 hover:shadow-xl transition-all hover-lift">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-amber-400 rounded-2xl flex items-center justify-center text-white mb-6">
                <svg className="h-10 w-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Education</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Tuition assistance, student loan repayment, and professional development.
              </p>
              <div className="text-green-600 font-mono font-bold text-lg">
                Save up to $5,250/year
              </div>
            </div>

            {/* Wellness */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:border-blue-600 hover:shadow-xl transition-all hover-lift">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-400 rounded-2xl flex items-center justify-center text-white mb-6">
                <svg className="h-10 w-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Wellness</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Gym memberships, mental health support, and wellness programs.
              </p>
              <div className="text-green-600 font-mono font-bold text-lg">
                Save up to $1,200/year
              </div>
            </div>

            {/* Transportation */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:border-blue-600 hover:shadow-xl transition-all hover-lift">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-600 to-teal-400 rounded-2xl flex items-center justify-center text-white mb-6">
                <svg className="h-10 w-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Transportation</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Pre-tax commuter benefits, parking, and transit passes.
              </p>
              <div className="text-green-600 font-mono font-bold text-lg">
                Save up to $3,000/year
              </div>
            </div>

            {/* Life Insurance */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:border-blue-600 hover:shadow-xl transition-all hover-lift">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-indigo-400 rounded-2xl flex items-center justify-center text-white mb-6">
                <svg className="h-10 w-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Life Insurance</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Term life, whole life, and disability insurance options.
              </p>
              <div className="text-green-600 font-mono font-bold text-lg">
                Save up to $800/year
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Preview */}
      <section id="calculator" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BenefitsCalculator />
        </div>
      </section>


      {/* Newsletter Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Stay in the Know</h2>
            <p className="text-lg text-gray-600">Get the latest tips on maximizing your benefits delivered to your inbox.</p>
          </div>
          <div className="max-w-xl mx-auto">
            <NewsletterSignup />
          </div>
        </div>
      </section>

      {/* CTA Section */}

      <section className="pt-32 pb-16 bg-gradient-hero text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Maximize Your Benefits?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of employees saving money on benefits.
          </p>
          <a href="/getstarted" className="inline-block bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-xl font-bold text-lg shadow-xl transition-all">
            Get Started Free
          </a>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
